import type { SceneCategory, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { Toast } from 'js/src/components/common/Toast';
import { removeLexicalEditor } from 'js/src/data/transcript/transcript-actions';
import { MAX_SCENE_GROUPS_IN_VIDEO } from 'js/src/libs/consts';
import FocusAndScroll from 'js/src/libs/focus-and-scroll';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import {
    deleteSceneGroupOperation,
    duplicateSceneGroupOperation,
    getPrePackagedMorphPosition,
    getPrePackagedSceneSequenceSceneGroupIds,
    getSceneGroupCategory,
} from 'js/src/luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

const CONTENT_SCENE_CATEGORIES: SceneCategory[] = [
    VideoData.CATEGORY_ORDINARY,
    VideoData.CATEGORY_TALKING_HEAD,
    VideoData.CATEGORY_TALKING_HEAD_INSERT,
    VideoData.CATEGORY_VOICEOVER,
];

export const SharedLeftControlHandlers = {
    handleDuplicateSceneGroup: ({
        numSceneGroups,
        duplicateSceneGroup,
        sceneGroupId,
    }: {
        numSceneGroups: number;
        duplicateSceneGroup: (args: { sceneGroupId: string }) => void;
        sceneGroupId: string;
    }) => {
        if (numSceneGroups >= MAX_SCENE_GROUPS_IN_VIDEO) {
            Toast.error(
                `You can have a maximum of ${MAX_SCENE_GROUPS_IN_VIDEO} scenes in your video. To create another scene, you must delete an existing scene.`,
            );
            return;
        }
        duplicateSceneGroup({ sceneGroupId });
    },
    handleMoveUpClicked: ({
        sceneGroupIndex,
        setFocusedSceneInfo,
        moveSceneGroup,
        sceneId,
        sceneGroupId,
        previousSceneId,
    }: {
        sceneGroupIndex: number;
        setFocusedSceneInfo: (sceneInfo: { sceneId: string; sceneGroupId: string }) => void;
        moveSceneGroup: (sceneGroupId: string, targetIndex: number) => void;
        sceneId: string;
        sceneGroupId: string;
        previousSceneId: string | null;
    }) => {
        // if the slide is the top slide, don't move!
        if (sceneGroupIndex <= 0) {
            return;
        }

        const targetIndex = sceneGroupIndex - 1;
        setFocusedSceneInfo({ sceneId, sceneGroupId });
        moveSceneGroup(sceneGroupId, targetIndex);

        // preemptively scroll to the previous slide to make a slick animation
        if (previousSceneId) {
            FocusAndScroll.scrollToSlide(previousSceneId);
        }
    },
    handleMoveDownClicked: ({
        isLastMovableScene,
        sceneGroupIndex,
        setFocusedSceneInfo,
        moveSceneGroup,
        nextSlideId,
        sceneGroupId,
        sceneId,
    }: {
        isLastMovableScene: boolean;
        sceneGroupIndex: number;
        setFocusedSceneInfo: (sceneInfo: { sceneId: string; sceneGroupId: string }) => void;
        moveSceneGroup: (sceneGroupId: string, targetIndex: number) => void;
        nextSlideId: string | null;
        sceneGroupId: string;
        sceneId: string;
    }) => {
        if (isLastMovableScene) {
            return;
        }

        const targetIndex = sceneGroupIndex + 1;
        setFocusedSceneInfo({ sceneId, sceneGroupId });
        moveSceneGroup(sceneGroupId, targetIndex);

        // preemptively scroll to the next slide to make a slick animation
        if (nextSlideId) {
            FocusAndScroll.scrollToSlide(nextSlideId);
        }
    },
    handleDeleteSceneGroup: createLuminaryOperation(
        (
            { call, dispatch, luminary },
            sceneGroupId: string,
            systemTriggered = false,
            parentActionGroup = uuid(),
        ) => {
            // Stop any previews, else any playing preview not related to the current slide will be mis-timed
            SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

            const chainIds = getPrePackagedSceneSequenceSceneGroupIds(luminary, sceneGroupId);

            const hasRemainingScene = (() => {
                const chainIdSet = new Set(chainIds);
                return luminary.sceneGroups.some(
                    (g) =>
                        !chainIdSet.has(g.id) &&
                        CONTENT_SCENE_CATEGORIES.includes(getSceneGroupCategory(g)),
                );
            })();

            if (!hasRemainingScene) {
                Toast.error(
                    'You must have at least one scene in your video! Create a new scene to delete this scene.',
                    'Oops!',
                );
                return;
            }

            for (const chainId of chainIds) {
                call(deleteSceneGroupOperation, chainId, systemTriggered, parentActionGroup);
                dispatch(removeLexicalEditor(chainId));
            }
        },
    ),
    duplicatePrePackagedSceneSequence: createLuminaryOperation(
        ({ luminary, call }, sceneGroupId: string) => {
            const chainIds = getPrePackagedSceneSequenceSceneGroupIds(luminary, sceneGroupId);
            if (luminary.sceneGroups.length + chainIds.length > MAX_SCENE_GROUPS_IN_VIDEO) {
                Toast.error(
                    `You can have a maximum of ${MAX_SCENE_GROUPS_IN_VIDEO} scenes in your video. To create another scene, you must delete an existing scene.`,
                );
                return;
            }

            SlideEventEmitter.emit(SLIDE_EVENTS.GLOBAL_STOP_PREVIEW_PLAYBACK);

            const lastChainGroupId = chainIds.at(-1);
            if (!lastChainGroupId) return;
            const lastChainGroup = luminary.getSceneGroupById(lastChainGroupId);
            if (!lastChainGroup) return;
            let insertIndex = lastChainGroup.index + 1;

            for (const sourceId of chainIds) {
                const newId = call(duplicateSceneGroupOperation, {
                    sceneGroupId: sourceId,
                    scrollToNewSceneGroup: false,
                });
                if (newId) {
                    luminary.moveSceneGroupToIndex(newId, insertIndex);
                    insertIndex++;
                }
            }
        },
    ),
    canMoveUp: ({ sceneGroup }: { sceneGroup: SceneGroup }) => {
        if (sceneGroup.index === 0) return false;
        // Prevent moving if this scene group is part of a pre-packaged scene sequence
        const firstScene = sceneGroup.scenes[0];
        if (firstScene) {
            const position = getPrePackagedMorphPosition(firstScene);
            if (position !== null) return false;
        }
        return true;
    },
    canMoveDown: ({ sceneGroup }: { sceneGroup: SceneGroup }) => {
        const luminary = sceneGroup.root;
        const isLastSceneGroup = sceneGroup.index === luminary.sceneGroups.length - 1;
        if (isLastSceneGroup) {
            return false;
        }
        // Prevent moving if this scene group is part of a pre-packaged scene sequence
        const firstScene = sceneGroup.scenes[0];
        if (firstScene) {
            const position = getPrePackagedMorphPosition(firstScene);
            if (position !== null) return false;
        }
        const nextSceneGroup = luminary.sceneGroups[sceneGroup.index + 1];
        return !nextSceneGroup.scenes.some(
            (scene: { sceneCategory: string }) =>
                scene.sceneCategory === VideoData.CATEGORY_BRAND ||
                scene.sceneCategory === VideoData.CATEGORY_OUTRO,
        );
    },
};
